package com.schoolconnect.app.feature.parentnotes

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogDefaultColors
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogSectionLabel
import com.schoolconnect.app.designsystem.AppDangerSoft
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.AppMuted
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.AppSurface
import com.schoolconnect.app.designsystem.AppSuccessSoft
import com.schoolconnect.app.designsystem.list.DesktopAwareLazyColumn
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.util.decodeImageBitmap
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.document
import ischoolconnect.sharedui.generated.resources.download
import ischoolconnect.sharedui.generated.resources.message
import ischoolconnect.sharedui.generated.resources.paperclip
import ischoolconnect.sharedui.generated.resources.user
import org.jetbrains.compose.resources.painterResource
import androidx.compose.material3.Icon
import androidx.compose.ui.platform.LocalUriHandler
import com.schoolconnect.app.datasource.network.response.ParentNoteReplyResponse
import com.schoolconnect.app.datasource.network.response.ParentNoteResponse

private data class ParentViewAttachment(
    val fileId: String,
    val url: String,
    val name: String,
    val mimeType: String,
    val sizeLabel: String,
    val previewBytes: ByteArray? = null
)

@Composable
internal fun ParentNoteViewDialog(
    row: ParentNoteRowUi?,
    detail: ParentNoteResponse?,
    replies: List<ParentNoteReplyResponse>,
    isLoading: Boolean,
    errorMessage: String,
    onDismiss: () -> Unit
) {
    if (row == null) return
    val historyState = rememberLazyListState()
    val uriHandler = LocalUriHandler.current
    val note = detail
    val displayStatus = note?.status.toDisplayStatus(row.status)
    var attachmentDetails by remember(note?.id, replies) { mutableStateOf(emptyMap<String, ParentViewAttachment>()) }
    LaunchedEffect(note?.id, replies) {
        val refs = buildList {
            note?.attachmentFileIds.orEmpty().forEachIndexed { index, id ->
                add(id to note?.attachmentFileUrls.orEmpty().getOrNull(index).orEmpty())
            }
            replies.forEach { reply ->
                reply.attachmentFileIds.orEmpty().forEachIndexed { index, id ->
                    add(id to reply.attachmentFileUrls.orEmpty().getOrNull(index).orEmpty())
                }
            }
        }.filter { it.first.isNotBlank() }.distinctBy { it.first }
        attachmentDetails = refs.associate { (fileId, responseUrl) ->
            val metadata = FileUploadRepository.getMetadata(fileId).getOrNull()
            val mimeType = metadata?.detectedMimeType ?: metadata?.declaredMimeType ?: "application/octet-stream"
            val url = responseUrl.ifBlank { FileUploadRepository.getDownloadUrl(fileId).getOrNull()?.url.orEmpty() }
            val previewBytes = if (mimeType.startsWith("image/", ignoreCase = true) && url.isNotBlank()) {
                FileUploadRepository.downloadBytes(url).getOrNull()
            } else null
            fileId to ParentViewAttachment(
                fileId = fileId,
                url = url,
                name = metadata?.originalFilename.orEmpty().ifBlank { "Attachment" },
                mimeType = mimeType,
                sizeLabel = metadata?.sizeBytes?.let(::formatAttachmentSize) ?: "File",
                previewBytes = previewBytes
            )
        }
    }
    AppDialogScaffold(onDismiss = onDismiss, size = com.schoolconnect.app.designsystem.AppDialogSize.Wide, maxDialogHeight = 860.dp, isLoading = isLoading, loadingTitle = "Loading parent note") {
        AppDialogHeader(
            title = "View Parent Conversation",
            subtitle = if (errorMessage.isBlank()) "" else errorMessage,
            titleSuffix = note?.parentNoteId ?: note?.id ?: row.parentNoteId,
            onClose = onDismiss,
            icon = Res.drawable.message,
            colors = AppDialogDefaultColors
        )
        Column(Modifier.fillMaxWidth().weight(1f).padding(horizontal = 24.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
            ParentViewPanel {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                    ParentViewMeta("Created", "Teacher", note?.createdAt.toDisplayTimestamp(), Modifier.weight(1f))
                    ParentViewMeta("Last Updated", "Teacher", note?.updatedAt.toDisplayTimestamp(), Modifier.weight(1f))
                }
            }
            ParentViewPanel {
                ParentViewSection("Conversation Information", Res.drawable.document)
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(32.dp)) {
                    Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        ParentViewValue("Student", note?.studentName.orEmpty().ifBlank { row.studentName })
                        ParentViewValue("Class", listOf(note?.className.orEmpty(), note?.sectionName.orEmpty()).filter { it.isNotBlank() }.joinToString("-").ifBlank { "${row.className}-${row.section}" })
                        ParentViewValue("Student ID", note?.studentId.orEmpty().ifBlank { "—" })
                        ParentViewValue("Parent ID", note?.parentId.orEmpty().ifBlank { "—" })
                    }
                    Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        ParentViewValue("Title", note?.title.orEmpty().ifBlank { row.title })
                        ParentViewValue("Status", displayStatus, parentNoteStatusColor(displayStatus))
                    }
                }
            }
            ParentViewPanel {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                    ParentViewSection("Conversation History", Res.drawable.message)
                    Text("${replies.size + 1} Messages", color = AppMuted, fontSize = 12.sp)
                }
                DesktopAwareLazyColumn(Modifier.fillMaxWidth().height(360.dp), state = historyState, contentPadding = PaddingValues(end = 12.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    item(key = "note-${note?.id ?: row.backendId}") {
                        ParentViewMessage("T", "Teacher", "Teacher", note?.createdAt.toDisplayTimestamp(), note?.title.orEmpty().ifBlank { row.title }, note?.message.orEmpty().ifBlank { row.detail }, AppPrimary, note?.attachmentFileIds.orEmpty().mapNotNull { attachmentDetails[it] }, uriHandler::openUri)
                    }
                    items(replies.size, key = { index -> replies[index].id ?: "reply-$index" }) { index ->
                        val reply = replies[index]
                        val isParent = reply.senderType.equals("PARENT", true)
                        ParentViewMessage(
                            initials = reply.senderName.orEmpty().initials(),
                            sender = reply.senderName.orEmpty().ifBlank { if (isParent) "Parent" else "Teacher" },
                            role = reply.senderType.orEmpty().ifBlank { "Reply" },
                            timestamp = reply.createdAt.toDisplayTimestamp(),
                            title = "",
                            body = reply.message.orEmpty(),
                            accent = if (isParent) Color(0xFF16A34A) else AppPrimary,
                            attachments = reply.attachmentFileIds.orEmpty().mapNotNull { attachmentDetails[it] },
                            onOpenUrl = uriHandler::openUri
                        )
                    }
                }
            }
        }
        AppDialogDivider()
        AppDialogFooter(actions = listOf(AppDialogFooterAction("Close", onDismiss, AppDialogActionStyle.Secondary, width = 120.dp)), colors = AppDialogDefaultColors)
    }
}

@Composable
private fun ParentViewPanel(content: @Composable ColumnScope.() -> Unit) {
    Column(Modifier.fillMaxWidth().border(BorderStroke(1.dp, AppDialogDefaultColors.border), RoundedCornerShape(8.dp)).padding(12.dp), verticalArrangement = Arrangement.spacedBy(12.dp), content = content)
}

@Composable
private fun ParentViewMeta(label: String, name: String, timestamp: String, modifier: Modifier) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(5.dp)) {
        Text(label, color = AppNavy, fontSize = 12.sp, fontWeight = FontWeight.ExtraBold)
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) { Icon(painterResource(Res.drawable.user), null, tint = AppNavy, modifier = Modifier.size(20.dp)); Text(name, color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.Bold) }
        Text(timestamp, color = AppMuted, fontSize = 12.sp)
    }
}

@Composable
private fun ParentViewSection(title: String, icon: org.jetbrains.compose.resources.DrawableResource) {
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(9.dp)) { Icon(painterResource(icon), null, tint = AppPrimary, modifier = Modifier.size(21.dp)); AppDialogSectionLabel(title, colors = AppDialogDefaultColors, fontSize = 17.sp) }
}

@Composable
private fun ParentViewValue(label: String, value: String, color: Color = AppNavy) {
    Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) { Text(label, color = AppNavy, fontSize = 13.sp, modifier = Modifier.width(75.dp)); Text(":", color = AppNavy, fontSize = 13.sp); Text(value, color = color, fontSize = 13.sp, fontWeight = FontWeight.SemiBold) }
}

@Composable
private fun ParentViewMessage(initials: String, sender: String, role: String, timestamp: String, title: String, body: String, accent: Color, attachments: List<ParentViewAttachment>, onOpenUrl: (String) -> Unit) {
    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(5.dp)) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) { Text(initials, color = accent, fontWeight = FontWeight.ExtraBold, modifier = Modifier.border(1.dp, accent.copy(alpha = .25f), RoundedCornerShape(50)).padding(9.dp)); Text(sender, color = AppNavy, fontWeight = FontWeight.ExtraBold, fontSize = 13.sp); Text("($role)", color = AppMuted, fontSize = 12.sp) }
            Text(timestamp, color = AppMuted, fontSize = 11.sp)
        }
        if (title.isNotBlank()) Text(title, color = AppNavy, fontWeight = FontWeight.ExtraBold, fontSize = 14.sp)
        Text(body, color = AppNavy, fontSize = 13.sp, lineHeight = 19.sp)
        attachments.forEach { attachment -> ParentViewAttachmentRow(attachment, onOpenUrl) }
    }
}

@Composable
private fun ParentViewAttachmentRow(attachment: ParentViewAttachment, onOpenUrl: (String) -> Unit) {
    val kind = attachmentKind(attachment)
    val image = remember(attachment.fileId, attachment.previewBytes) { attachment.previewBytes?.let(::decodeImageBitmap) }
    val background = if (kind == "IMAGE") AppSuccessSoft else AppDangerSoft
    Row(Modifier.fillMaxWidth().border(1.dp, Color(0xFFD8E0EF), RoundedCornerShape(8.dp)).padding(10.dp), verticalAlignment = Alignment.CenterVertically) {
        if (image != null) {
            Image(image, contentDescription = attachment.name, modifier = Modifier.size(52.dp).background(AppSurface, RoundedCornerShape(8.dp)), contentScale = ContentScale.Crop)
        } else {
            androidx.compose.foundation.layout.Box(Modifier.size(34.dp).background(background, RoundedCornerShape(8.dp)), contentAlignment = Alignment.Center) {
                Icon(painterResource(Res.drawable.document), null, tint = AppPrimary, modifier = Modifier.size(20.dp))
            }
        }
        androidx.compose.foundation.layout.Spacer(Modifier.width(10.dp))
        androidx.compose.foundation.layout.Column(Modifier.weight(1f)) {
            Text(attachment.name, color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.Medium)
            Text("${kindLabel(kind, attachment)}  •  ${attachment.sizeLabel}", color = AppMuted, fontSize = 11.sp)
        }
        Icon(painterResource(Res.drawable.download), "Download ${attachment.name}", tint = if (attachment.url.isNotBlank()) AppPrimary else AppMuted, modifier = Modifier.size(20.dp).then(if (attachment.url.isBlank()) Modifier else Modifier.clickable { onOpenUrl(attachment.url) }))
    }
}

private fun attachmentKind(attachment: ParentViewAttachment): String = when {
    attachment.mimeType.startsWith("image/", ignoreCase = true) -> "IMAGE"
    attachment.mimeType.contains("pdf", ignoreCase = true) -> "PDF"
    else -> attachment.name.substringAfterLast('.', "FILE").uppercase().ifBlank { "FILE" }
}

private fun kindLabel(kind: String, attachment: ParentViewAttachment): String = when (kind) {
    "IMAGE" -> "Image"
    "PDF" -> "PDF"
    else -> attachment.mimeType.substringAfterLast('/', kind).uppercase()
}

private fun formatAttachmentSize(bytes: Long): String = when {
    bytes >= 1024L * 1024L -> "${bytes / (1024L * 1024L)} MB"
    bytes >= 1024L -> "${bytes / 1024L} KB"
    else -> "$bytes B"
}

private fun String?.toDisplayTimestamp(): String = this.orEmpty().replace('T', ' ').substringBefore('+').ifBlank { "—" }

private fun String?.toDisplayStatus(fallback: String): String =
    com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.label("PARENT_NOTE_STATUS", this.orEmpty().ifBlank { fallback })

private fun String.initials(): String = trim().split(Regex("\\s+")).filter { it.isNotBlank() }.take(2).joinToString("") { it.first().uppercase() }.ifBlank { "R" }
